import { useContext, useMemo, useState } from 'react'
import Layout from 'shared/layouts/Main'
import useInit from './hooks/useInit'
import getRows from './utils/getRows'
import DeleteModal from 'shared/components/DeleteModal'
import { apiUrls, tableColumnWidths, roles } from 'consts'
import {
  PagingState,
  SearchState,
  IntegratedFiltering,
  SortingState,
  DataTypeProvider,
  IntegratedPaging,
} from '@devexpress/dx-react-grid'
import {
  Grid,
  SearchPanel,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  Toolbar,
  PagingPanel,
} from '@devexpress/dx-react-grid-bootstrap4'
import { TableColumnWidthInfo } from '@devexpress/dx-react-grid/dist/dx-react-grid'
import HeaderCustomerSelect from 'containers/HeaderCustomerSelect'
import hasAnyRole from 'shared/utils/hasAnyRole'
import getBrandContent from 'utils/getBrandContent'
import SearchPanelInput from 'shared/components/SearchPanelInput'
import Footer from 'shared/components/Footer'
import { formatDate, stringToDate } from 'utils/dateUtils'
import { ReactReduxContext } from 'react-redux'
import LayoutSwitcher from 'components/LayoutSwitcher'
import useReorder from 'hooks/useReorder'
import reorderRows from './utils/reorderRows'
import {
  DndContext,
  closestCenter,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ComponentType } from '@react-spring/web'
import SortableItem from 'components/SortableItem'
import SortableTableCell from 'components/SortableTableCell'
import SortableOverLayContent from 'components/SortableOverlayContent'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

const ViewSections: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [rows, setRows] = useState<any>([])
  const [showModal, setShowModal] = useState<boolean>(false)
  const [filter, setFilter] = useState<object | null>(null)
  const [deleteEntryId, setDeleteEntryId] = useState<boolean | number>(false)
  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})
  const [filterVisible, setFilterVisible] = useState<boolean>(false)
  const [dragEndEvent, setDragEndEvent] = useState<any | null>(null)
  const sortableItems = useMemo(() => rows?.map(({ id }) => id), [rows])

  const { store } = useContext(ReactReduxContext)

  const columns: { name: string; title: string; encodeHtml?: boolean; getCellValue?: any }[] = [
    { name: 'sortColumn', title: ' ' },
    { name: 'name', title: 'Name' },
    { name: 'superadmin_only', title: 'Superadmin Only' },
    { name: 'active', title: 'Active' },
    { name: 'action', title: 'Action', encodeHtml: false },
  ]

  const columnWidths: TableColumnWidthInfo[] = [
    { columnName: 'sortColumn', width: tableColumnWidths.sortColumn },
    { columnName: 'name', width: 'auto' },
    { columnName: 'superadmin_only', width: 'auto' },
    { columnName: 'active', width: tableColumnWidths.active },
    { columnName: 'action', width: tableColumnWidths.action },
  ]

  useInit({
    setRows,
    setDeleteEntryId,
    setShowModal,
    setShowOverlay,
    filter,
    currentCustomer
  })

  useReorder({
    rows,
    dragEndEvent,
    setRows,
    reorderRows,
    sortRowsUrl: apiUrls.sections + '/' + apiUrls.actionSort,
  })

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}))

  const TableRow: ComponentType<any> | undefined = ({ row, ...restProps }) => {
    return (
      <SortableItem key={rows.indexOf(row)} index={rows.indexOf(row)} row={row} {...restProps} />
    )
  }

  const TableCell: any = ({ value, ...rest }) => {
    return (
      <SortableTableCell value={value} tableCellProps={rest} draggableColumnName='sortColumn' />
    )
  }

  const TableComponent = ({ ...restProps }) => (
    <Table.Table {...restProps} className='small with-action min-mobile-width' />
  )
  const isAdmin: boolean = hasAnyRole([roles.admin, roles.superAdmin])

  const [dateFormatter] = useState<any>({
    for: ['last_used_date'],
    formatterComponent: (props) => formatDate(stringToDate(props.row.last_used_date)),
    editorComponent: undefined,
    availableFilterOperations: undefined,
  })

  const SearchPanelInputComponent = (props) => {
    return SearchPanelInput({
      addUrl: isAdmin ? '/add-section' : undefined,
      addText: isAdmin ? 'Add Section' : undefined,
      inputProps: props,
    })
  }

  const PagingContainer = (props) => {
    return <PagingPanel.Container {...props} className='table-footer' />
  }

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      headerInsert={
        <>
          {hasAnyRole([roles.superAdmin]) && (
            <HeaderCustomerSelect
              setShowOverlay={setShowOverlay}
              handleCustomerChanged={(customer) => setCurrentCustomer(customer)}
            />
          )}
        </>
      }
      menuBottomInsert={hasAnyRole([roles.superAdmin]) ? <LayoutSwitcher store={store} /> : false}
    >
      <>
        <DndContext
          sensors={sensors}
          onDragEnd={(e) => setDragEndEvent(e)}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
            <Grid rows={rows} columns={columns}>
              <DataTypeProvider {...dateFormatter} />
              <SortingState columnSortingEnabled={false} defaultSorting={[{ columnName: 'sortColumn', direction: 'asc' }]} />
              <SearchState defaultValue='' />
              <IntegratedFiltering />
              <PagingState defaultCurrentPage={0} pageSize={0} />
              <IntegratedPaging />
              <Toolbar />
              <SearchPanel inputComponent={SearchPanelInputComponent} />
              <Table
                tableComponent={TableComponent}
                cellComponent={TableCell}
                rowComponent={TableRow}
              />
              <TableColumnResizing defaultColumnWidths={columnWidths} resizingMode='nextColumn' />
              <TableHeaderRow showSortingControls />
              <PagingPanel pageSizes={[]} containerComponent={PagingContainer} />
            </Grid>

            <DeleteModal
              url={apiUrls.sections}
              showModal={showModal}
              setShowModal={setShowModal}
              deleteEntryId={deleteEntryId}
              setDeleteEntryId={setDeleteEntryId}
              entityName='Section'
              customConfirmMessage={{
                rows,
                confirmMessage: (row) => {
                  let confirmMessage = 'Are you sure you want to delete this Section'

                  const entitiesIncluded: Array<string> = []

                  if (row.includes_pages) {
                    entitiesIncluded.push('Pages')
                  }

                  if (entitiesIncluded.length > 0) {
                    confirmMessage += ' with all included ' + entitiesIncluded.join(' and ')
                  }
                  confirmMessage += '? '

                  const includedEntites: Array<string> = []
                  if (row.is_included_in_permissions) {
                    includedEntites.push('Permission(s)')
                  }

                  if (includedEntites.length > 0) {
                    confirmMessage +=
                      'Note that this Section is also included in ' +
                      includedEntites.join(' and ') +
                      '.'
                  }

                  return confirmMessage
                },
              }}
              getRows={getRows}
              setRows={setRows}
            />
          </SortableContext>
          <DragOverlay>
            <SortableOverLayContent />
          </DragOverlay>
        </DndContext>
      </>
    </Layout>
  )
}

export default ViewSections
